import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { useContent } from "../context/ContentContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

export default function ContactPage() {
  const { content, loading } = useContent();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", lastname: "", company: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Faltan campos", description: "Nombre, email y mensaje son obligatorios." });
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/contact", form);
      setSent(true);
      toast({ title: "Mensaje enviado", description: "Nos pondremos en contacto pronto." });
      setForm({ name: "", lastname: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "No se pudo enviar el mensaje." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white/60 font-mono-caps tracking-widest">
        CARGANDO...
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
      <Header />

      <section className="relative pt-40 pb-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-[#ff6a2b]/10 via-black to-black" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-white/50 text-center">
            — Contacto —
          </p>
          <h1 className="mt-6 text-center font-display font-black text-[38px] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
            Hablemos de <span className="text-gradient-music">música</span>{" "}
            <span className="text-white">y</span> <span className="text-gradient-teams">equipos</span>.
          </h1>
          <p className="mt-8 max-w-2xl mx-auto text-center text-white/70 text-lg md:text-2xl font-medium">
            Cuéntanos sobre tu proyecto y te contactamos en menos de 24 horas.
          </p>
        </div>
      </section>

      <section className="relative bg-black pb-24 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Info panel */}
          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-gradient-to-br from-neutral-950 to-black p-8">
            <h3 className="font-display font-bold text-2xl text-white leading-tight">
              <span className="text-gradient-music">Contacto</span> directo
            </h3>
            <p className="mt-4 text-white/60 text-sm leading-relaxed">
              Estamos en Bogotá, pero producimos y viajamos para toda América Latina.
            </p>
            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#ff6a2b]/15 border border-[#ff6a2b]/30 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#ff8a3c]" />
                </div>
                <div>
                  <p className="font-mono-caps text-xs tracking-[0.3em] text-white/40 uppercase">Email</p>
                  <p className="text-white mt-1">{content.footer.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#2ec9a8]/15 border border-[#2ec9a8]/30 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#2ec9a8]" />
                </div>
                <div>
                  <p className="font-mono-caps text-xs tracking-[0.3em] text-white/40 uppercase">Teléfono</p>
                  <p className="text-white mt-1">{content.footer.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/15 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-mono-caps text-xs tracking-[0.3em] text-white/40 uppercase">Ubicación</p>
                  <p className="text-white mt-1">{content.footer.location}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-3 rounded-2xl border border-white/10 bg-neutral-950/60 p-8 md:p-10 space-y-5">
            {sent && (
              <div className="flex items-start gap-3 rounded-xl border border-[#2ec9a8]/40 bg-[#2ec9a8]/10 p-4">
                <CheckCircle2 className="w-5 h-5 text-[#2ec9a8] mt-0.5" />
                <div>
                  <p className="text-white font-medium">¡Mensaje recibido!</p>
                  <p className="text-white/60 text-sm mt-1">Te contactaremos pronto. Si es urgente, escríbenos por WhatsApp.</p>
                </div>
              </div>
            )}

            <div>
              <label className="block font-mono-caps text-xs tracking-[0.3em] uppercase text-white/50 mb-2">Empresa</label>
              <input value={form.company} onChange={onChange("company")} className="w-full bg-black/60 border border-white/10 focus:border-[#ff8a3c] focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors" placeholder="Opcional" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono-caps text-xs tracking-[0.3em] uppercase text-white/50 mb-2">Nombre</label>
                <input value={form.name} onChange={onChange("name")} required data-testid="contact-name" className="w-full bg-black/60 border border-white/10 focus:border-[#ff8a3c] focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors" placeholder="Tu nombre" />
              </div>
              <div>
                <label className="block font-mono-caps text-xs tracking-[0.3em] uppercase text-white/50 mb-2">Apellido</label>
                <input value={form.lastname} onChange={onChange("lastname")} data-testid="contact-lastname" className="w-full bg-black/60 border border-white/10 focus:border-[#ff8a3c] focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors" placeholder="Tu apellido" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono-caps text-xs tracking-[0.3em] uppercase text-white/50 mb-2">Email</label>
                <input type="email" value={form.email} onChange={onChange("email")} required data-testid="contact-email" className="w-full bg-black/60 border border-white/10 focus:border-[#ff8a3c] focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors" placeholder="tu@email.com" />
              </div>
              <div>
                <label className="block font-mono-caps text-xs tracking-[0.3em] uppercase text-white/50 mb-2">Teléfono</label>
                <input type="tel" value={form.phone} onChange={onChange("phone")} data-testid="contact-phone" className="w-full bg-black/60 border border-white/10 focus:border-[#ff8a3c] focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors" placeholder="+57 300 000 0000" />
              </div>
            </div>
            <div>
              <label className="block font-mono-caps text-xs tracking-[0.3em] uppercase text-white/50 mb-2">Mensaje</label>
              <textarea value={form.message} onChange={onChange("message")} required rows={5} data-testid="contact-message" className="w-full bg-black/60 border border-white/10 focus:border-[#ff8a3c] focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 resize-none transition-colors" placeholder="Cuéntanos sobre tu proyecto..." />
            </div>
            <div className="pt-2">
              <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-shine inline-flex items-center gap-3 rounded-full bg-[#ff6a2b] hover:bg-[#ff8a3c] text-black pl-6 pr-5 py-3.5 font-mono-caps text-xs tracking-[0.2em] uppercase transition-colors disabled:opacity-60">
                {submitting ? "Enviando..." : "Enviar mensaje"}
                <span className="w-7 h-7 rounded-full bg-black text-white flex items-center justify-center">
                  <Send className="w-3.5 h-3.5" />
                </span>
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer footer={content.footer} />
      <WhatsAppButton />
    </div>
  );
}
